import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Archive, Bell, Building2, CalendarClock, ChevronDown, ChevronRight, Database, Group, GitBranch, Handshake, History, ListChecks, ListTodo, LogOut, Menu, PieChart, Send, Settings, Sliders, StickyNote, Upload, Users, UserCog,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import PWAInstall from "@/components/PWAInstall";
import NotificationBell from "@/components/NotificationBell";

const navItem = (active) =>
  `flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
    active ? "bg-gold text-ink" : "text-slate-300 hover:bg-white/5 hover:text-white"
  }`;

function NotifNavBadge() {
  const { data } = useQuery({
    queryKey: ["notif-unread-count"],
    queryFn: async () => (await api.get("/notifications/unread-count")).data,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });
  const count = data?.count || 0;
  if (count <= 0) return null;
  return (
    <span
      className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center"
      data-testid="nav-notifications-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function TasksNavBadge() {
  const location = useLocation();
  const { data, refetch } = useQuery({
    queryKey: ["tasks-badge-count"],
    queryFn: async () => (await api.get("/tasks/badge-count")).data,
    refetchOnMount: "always",
  });
  React.useEffect(() => { refetch(); }, [location.pathname, refetch]);
  const count = data?.count || 0;
  if (count <= 0) return null;
  return (
    <span
      className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#A85A3C] text-white text-[11px] font-bold flex items-center justify-center"
      data-testid="tasks-nav-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-gold text-ink font-serif text-xl font-semibold leading-none" aria-hidden="true">3</span>
      <span className="font-serif text-2xl text-white leading-none">Realty</span>
    </div>
  );
}

const pathInPrefixes = (pathname, prefixes) =>
  prefixes.some((p) => pathname === p || pathname.startsWith(p + "/"));

// Collapsible sidebar group. Header toggles; it is never a nav link and has no active state.
// Auto-expands (regardless of toggle) whenever the active route lives inside it.
function NavGroup({ label, groupKey, prefixes, expanded, onToggle, pathname, onNavigate, children }) {
  const hasActive = pathInPrefixes(pathname, prefixes);
  const isOpen = expanded || hasActive;
  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => onToggle(groupKey)}
        className="w-full flex items-center justify-between px-4 py-2 min-h-[44px] text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors"
        data-testid={`nav-group-${groupKey}`}
        aria-expanded={isOpen}
      >
        <span>{label}</span>
        {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {isOpen && <div className="space-y-1" data-testid={`nav-group-items-${groupKey}`}>{children}</div>}
    </div>
  );
}

// Sub-item within a group: same look as top-level items, indented to show hierarchy.
const subNavItem = (active) => `${navItem(active)} !pl-8`;

function SidebarContent({ user, isAdmin, isSM, isRM, roleLabel, initials, onLogout, onNavigate, showBell, expanded, onToggleGroup }) {
  const location = useLocation();
  const pathname = location.pathname;
  const assetsVisible = isAdmin || isSM;
  const leadsVisible = isAdmin || isRM;
  const clientsVisible = isAdmin || isRM;
  const brokersVisible = isAdmin || isRM || isSM;
  return (
    <div className="flex flex-col h-full bg-ink">
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-start justify-between gap-2">
          <Brand />
          {showBell && <NotificationBell variant="dark" />}
        </div>
        <div className="text-xs text-slate-400 mt-2 tracking-wide">SARFAESI · ARC · NCLT — Maharashtra</div>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto scrollbar-thin">
        <NavLink to="/" end onClick={onNavigate} className={({ isActive }) => navItem(isActive)} data-testid="nav-dashboard">
          <Bell className="h-4 w-4" />
          <span>Dashboard</span>
        </NavLink>

        <NavLink to="/reports" onClick={onNavigate} className={({ isActive }) => navItem(isActive)} data-testid="nav-reports">
          <PieChart className="h-4 w-4" />
          <span>Reports</span>
        </NavLink>

        {(isAdmin || isRM) && (
          <NavLink to="/meetings/new" onClick={onNavigate} className={({ isActive }) => navItem(isActive)} data-testid="nav-log-meeting">
            <CalendarClock className="h-4 w-4" />
            <span>Log Meeting</span>
          </NavLink>
        )}

        <NavLink to="/notifications" onClick={onNavigate} className={({ isActive }) => navItem(isActive)} data-testid="nav-notifications">
          <Bell className="h-4 w-4" />
          <span>Notifications</span>
          <NotifNavBadge />
        </NavLink>

        <NavLink to="/notes" onClick={onNavigate} className={({ isActive }) => navItem(isActive)} data-testid="nav-notes">
          <StickyNote className="h-4 w-4" />
          <span>Notes</span>
        </NavLink>

        {assetsVisible && (
          <NavGroup label="Assets" groupKey="assets" prefixes={["/assets/auction", "/assets/land", "/assets/archived"]} expanded={expanded.assets} onToggle={onToggleGroup} pathname={pathname} onNavigate={onNavigate}>
            <NavLink to="/assets/auction" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-auction-assets">
              <Building2 className="h-4 w-4" /><span>Auction Assets</span>
            </NavLink>
            <NavLink to="/assets/land" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-land-assets">
              <Building2 className="h-4 w-4" /><span>Land &amp; Other Assets</span>
            </NavLink>
            <NavLink to="/assets/archived" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-archived-assets">
              <Building2 className="h-4 w-4" /><span>Archived Assets</span>
            </NavLink>
          </NavGroup>
        )}

        {leadsVisible && (
          <NavGroup label="Leads" groupKey="leads" prefixes={["/leads", "/lead-groups", "/kanban"]} expanded={expanded.leads} onToggle={onToggleGroup} pathname={pathname} onNavigate={onNavigate}>
            <NavLink to="/leads" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-leads">
              <Users className="h-4 w-4" /><span>Leads</span>
            </NavLink>
            <NavLink to="/lead-groups" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-lead-groups">
              <Group className="h-4 w-4" /><span>Lead Groups</span>
            </NavLink>
            <NavLink to="/kanban" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-kanban">
              <GitBranch className="h-4 w-4" /><span>Lead Pipeline</span>
            </NavLink>
          </NavGroup>
        )}

        {clientsVisible && (
          <NavGroup label="Clients" groupKey="clients" prefixes={["/clients", "/client-groups", "/client-pipeline"]} expanded={expanded.clients} onToggle={onToggleGroup} pathname={pathname} onNavigate={onNavigate}>
            <NavLink to="/clients" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-clients">
              <Building2 className="h-4 w-4" /><span>Clients</span>
            </NavLink>
            <NavLink to="/client-groups" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-client-groups">
              <Group className="h-4 w-4" /><span>Client Groups</span>
            </NavLink>
            <NavLink to="/client-pipeline" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-client-pipeline">
              <GitBranch className="h-4 w-4" /><span>Client Pipeline</span>
            </NavLink>
          </NavGroup>
        )}

        {brokersVisible && (
          <NavGroup label="Brokers" groupKey="brokers" prefixes={["/brokers/retail", "/brokers/icp", "/brokers/archived"]} expanded={expanded.brokers} onToggle={onToggleGroup} pathname={pathname} onNavigate={onNavigate}>
            <NavLink to="/brokers/retail" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-retail-brokers">
              <Handshake className="h-4 w-4" /><span>Retail Brokers</span>
            </NavLink>
            <NavLink to="/brokers/icp" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-icp-brokers">
              <Handshake className="h-4 w-4" /><span>ICP Brokers</span>
            </NavLink>
            {(isAdmin || isRM) && (
              <NavLink to="/brokers/archived" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-archived-brokers">
                <Archive className="h-4 w-4" /><span>Archived Brokers</span>
              </NavLink>
            )}
          </NavGroup>
        )}

        {(isAdmin || isRM) && (
          <NavLink to="/tasks" onClick={onNavigate} className={({ isActive }) => navItem(isActive)} data-testid="nav-tasks">
            <ListTodo className="h-4 w-4" />
            <span>Tasks</span>
            <TasksNavBadge />
          </NavLink>
        )}

        {(isAdmin || isRM) && (
          <NavLink to="/bulk-import" onClick={onNavigate} className={({ isActive }) => navItem(isActive)} data-testid="nav-bulk-import">
            <Upload className="h-4 w-4" />
            <span>Bulk Import</span>
          </NavLink>
        )}

        {isAdmin && (
          <NavGroup label="Admin" groupKey="admin" prefixes={["/master-data", "/taxonomy-cleanup", "/lead-source-mappings", "/match-settings", "/match-config", "/users", "/system-settings", "/audit-log"]} expanded={expanded.admin} onToggle={onToggleGroup} pathname={pathname} onNavigate={onNavigate}>
            <NavLink to="/master-data" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-master-data">
              <Database className="h-4 w-4" /><span>Master Data</span>
            </NavLink>
            <NavLink to="/taxonomy-cleanup" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-taxonomy-cleanup">
              <ListChecks className="h-4 w-4" /><span>Cleanup Queue</span>
            </NavLink>
            <NavLink to="/lead-source-mappings" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-lead-source-mappings">
              <Send className="h-4 w-4" /><span>Inbound Mappings</span>
            </NavLink>
            <NavLink to="/match-settings" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-match-settings">
              <Sliders className="h-4 w-4" /><span>Match Probabilities</span>
            </NavLink>
            <NavLink to="/match-config" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-match-config">
              <Sliders className="h-4 w-4" /><span>Matching Config</span>
            </NavLink>
            <NavLink to="/users" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-users">
              <UserCog className="h-4 w-4" /><span>Users &amp; Roles</span>
            </NavLink>
            <NavLink to="/system-settings" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-system-settings">
              <Settings className="h-4 w-4" /><span>System Settings</span>
            </NavLink>
            <NavLink to="/audit-log" onClick={onNavigate} className={({ isActive }) => subNavItem(isActive)} data-testid="nav-audit-log">
              <History className="h-4 w-4" /><span>Audit Log</span>
            </NavLink>
          </NavGroup>
        )}
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-gold text-ink text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate" data-testid="current-user-name">
              {user?.name}
            </div>
            <div className="text-xs text-slate-400 truncate">{roleLabel}</div>
          </div>
        </div>
        <Button variant="ghost" size="sm" className="w-full justify-start mt-2 text-slate-300 hover:text-white hover:bg-white/5" onClick={onLogout} data-testid="logout-button">
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </Button>
        <PWAInstall />
      </div>
    </div>
  );
}

const GROUP_DEFAULTS = { assets: true, leads: true, clients: false, brokers: false, admin: false };
const NAV_GROUPS_KEY = "3realty-nav-groups";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try {
      const s = sessionStorage.getItem(NAV_GROUPS_KEY);
      if (s) return { ...GROUP_DEFAULTS, ...JSON.parse(s) };
    } catch { /* ignore */ }
    return GROUP_DEFAULTS;
  });
  useEffect(() => {
    try { sessionStorage.setItem(NAV_GROUPS_KEY, JSON.stringify(expanded)); } catch { /* ignore */ }
  }, [expanded]);
  const onToggleGroup = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  const roles = user?.roles || [user?.role];
  const isAdmin = roles.includes("admin");
  const isSM = roles.includes("sourcing_manager");
  const isRM = roles.includes("relationship_manager");

  const onLogout = async () => {
    setMobileOpen(false);
    try { sessionStorage.removeItem(NAV_GROUPS_KEY); } catch { /* ignore */ }
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "?")
    .split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const roleLabel = isAdmin
    ? "Admin"
    : isRM && isSM
      ? "Sourcing + RM"
      : isRM
        ? "Relationship Manager"
        : "Sourcing Manager";

  const sidebarProps = { user, isAdmin, isSM, isRM, roleLabel, initials, onLogout, expanded, onToggleGroup };

  return (
    <div className="min-h-screen lg:flex app-grain">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center gap-2 bg-ink border-b border-white/10 min-h-14 pt-[env(safe-area-inset-top)] pl-[calc(0.75rem+env(safe-area-inset-left))] pr-[calc(1rem+env(safe-area-inset-right))]">
        <button
          onClick={() => setMobileOpen(true)}
          className="relative z-10 flex items-center justify-center h-12 w-12 shrink-0 text-slate-200 hover:text-white rounded-md hover:bg-white/5"
          aria-label="Open navigation menu"
          data-testid="mobile-menu-trigger"
        >
          <Menu className="h-6 w-6" />
        </button>
        <Brand />
        <div className="ml-auto">
          <NotificationBell variant="dark" />
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col">
        <SidebarContent {...sidebarProps} showBell />
      </aside>

      {/* Mobile slide-out drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 max-w-[85vw] p-0 border-none bg-ink text-white pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] [&>button]:text-white [&>button]:z-10" data-testid="mobile-nav-drawer">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarContent {...sidebarProps} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}
